import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ScenarioModeling = () => {
  const [scenario, setScenario] = useState({
    swapFeeIncrease: 0,
    merchantFeeIncrease: 0,
    volumeGrowth: 0,
    costReduction: 0
  });

  const [results, setResults] = useState(null);

  const baseMetrics = {
    currentRevenue: 2450000,
    currentCosts: 1715000,
    currentMargin: 30
  };

  const calculateScenario = () => {
    const revenueImpact = 
      (baseMetrics?.currentRevenue * 0.45 * (scenario?.swapFeeIncrease / 100)) +
      (baseMetrics?.currentRevenue * 0.35 * (scenario?.merchantFeeIncrease / 100)) +
      (baseMetrics?.currentRevenue * (scenario?.volumeGrowth / 100));

    const costImpact = 
      baseMetrics?.currentCosts * (scenario?.volumeGrowth / 100) -
      (baseMetrics?.currentCosts * (scenario?.costReduction / 100));

    const newRevenue = baseMetrics?.currentRevenue + revenueImpact;
    const newCosts = baseMetrics?.currentCosts + costImpact;
    const newMargin = ((newRevenue - newCosts) / newRevenue) * 100;

    setResults({
      projectedRevenue: newRevenue,
      projectedCosts: newCosts,
      projectedMargin: newMargin,
      revenueChange: revenueImpact,
      costChange: costImpact,
      marginChange: newMargin - baseMetrics?.currentMargin
    });
  };

  const handleInputChange = (field, value) => {
    setScenario(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const resetScenario = () => {
    setScenario({
      swapFeeIncrease: 0,
      merchantFeeIncrease: 0,
      volumeGrowth: 0,
      costReduction: 0
    });
    setResults(null);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Scenario Modeling
          </h3>
          <p className="caption text-muted-foreground">
            Project financial impact of strategic changes
          </p>
        </div>
        <Icon name="Calculator" size={20} className="text-primary" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Input
          label="Swap Fee Adjustment (%)"
          type="number"
          value={scenario?.swapFeeIncrease}
          onChange={(e) => handleInputChange('swapFeeIncrease', e?.target?.value)}
          placeholder="0.0"
          description="Positive or negative percentage change"
        />
        <Input
          label="Merchant Fee Adjustment (%)"
          type="number"
          value={scenario?.merchantFeeIncrease}
          onChange={(e) => handleInputChange('merchantFeeIncrease', e?.target?.value)}
          placeholder="0.0"
          description="Positive or negative percentage change"
        />
        <Input
          label="Volume Growth (%)"
          type="number"
          value={scenario?.volumeGrowth}
          onChange={(e) => handleInputChange('volumeGrowth', e?.target?.value)}
          placeholder="0.0"
          description="Expected transaction volume increase"
        />
        <Input
          label="Cost Reduction (%)"
          type="number"
          value={scenario?.costReduction}
          onChange={(e) => handleInputChange('costReduction', e?.target?.value)}
          placeholder="0.0"
          description="Operational cost savings target"
        />
      </div>
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="default"
          onClick={calculateScenario}
          iconName="Play"
          iconPosition="left"
        >
          Run Scenario
        </Button>
        <Button
          variant="outline"
          onClick={resetScenario}
          iconName="RotateCcw"
          iconPosition="left"
        >
          Reset
        </Button>
      </div>
      {results && (
        <div className="border-t border-border pt-6">
          <h4 className="text-base font-semibold text-foreground mb-4">Projected Results</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted/30">
              <p className="caption text-muted-foreground mb-2">Projected Revenue</p>
              <p className="text-xl md:text-2xl font-bold text-foreground font-data mb-1">
                ${results?.projectedRevenue?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <div className="flex items-center gap-1">
                <Icon 
                  name={results?.revenueChange >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                  size={14} 
                  className={results?.revenueChange >= 0 ? 'text-success' : 'text-error'}
                />
                <span className={`caption ${results?.revenueChange >= 0 ? 'text-success' : 'text-error'}`}>
                  ${Math.abs(results?.revenueChange)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/30">
              <p className="caption text-muted-foreground mb-2">Projected Costs</p>
              <p className="text-xl md:text-2xl font-bold text-foreground font-data mb-1">
                ${results?.projectedCosts?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <div className="flex items-center gap-1">
                <Icon 
                  name={results?.costChange <= 0 ? 'TrendingDown' : 'TrendingUp'} 
                  size={14} 
                  className={results?.costChange <= 0 ? 'text-success' : 'text-error'}
                />
                <span className={`caption ${results?.costChange <= 0 ? 'text-success' : 'text-error'}`}>
                  ${Math.abs(results?.costChange)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-muted/30">
              <p className="caption text-muted-foreground mb-2">Projected Margin</p>
              <p className="text-xl md:text-2xl font-bold text-foreground font-data mb-1">
                {results?.projectedMargin?.toFixed(1)}%
              </p>
              <div className="flex items-center gap-1">
                <Icon 
                  name={results?.marginChange >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                  size={14} 
                  className={results?.marginChange >= 0 ? 'text-success' : 'text-error'}
                />
                <span className={`caption ${results?.marginChange >= 0 ? 'text-success' : 'text-error'}`}>
                  {Math.abs(results?.marginChange)?.toFixed(1)}pp
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioModeling;